/**
 * LLM Event Emitter - v13 Section 10.4
 *
 * Lightweight typed EventEmitter for LLMClient usage tracking.
 * Zero dependencies, browser + Node.js compatible.
 *
 * @see docs/sprints/ownyou-sprint11i-spec.md
 *
 * @example
 * ```typescript
 * const emitter = new LLMEventEmitter();
 *
 * // Subscribe (returns unsubscribe function)
 * const unsubscribe = emitter.on('usage', (event) => {
 *   console.log(`Cost: $${event.costUsd}`);
 * });
 *
 * // Emit events
 * emitter.emit('usage', { model: 'gpt-4o-mini', costUsd: 0.001, ... });
 *
 * // Later: unsubscribe
 * unsubscribe();
 * ```
 */
/**
 * Lightweight typed EventEmitter for LLMClient
 */
export class LLMEventEmitter {
    constructor() {
        this.listeners = new Map();
    }
    /**
     * Subscribe to an event
     * @param event Event name
     * @param handler Event handler function
     * @returns Unsubscribe function
     */
    on(event, handler) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, new Set());
        }
        this.listeners.get(event).add(handler);
        // Return unsubscribe function
        return () => {
            this.listeners.get(event)?.delete(handler);
        };
    }
    /**
     * Emit an event to all subscribers
     * @param event Event name
     * @param data Event data
     */
    emit(event, data) {
        const handlers = this.listeners.get(event);
        if (!handlers)
            return;
        handlers.forEach((handler) => {
            try {
                handler(data);
            }
            catch (error) {
                console.error(`Error in ${String(event)} event handler:`, error);
            }
        });
    }
    /**
     * Remove a specific handler
     * @param event Event name
     * @param handler Handler to remove
     */
    off(event, handler) {
        this.listeners.get(event)?.delete(handler);
    }
    /**
     * Remove all handlers for an event (or all events)
     * @param event Event name (optional - if omitted, clears all)
     */
    clear(event) {
        if (event) {
            this.listeners.delete(event);
        }
        else {
            this.listeners.clear();
        }
    }
    /**
     * Get count of handlers for an event
     * @param event Event name
     * @returns Number of registered handlers
     */
    listenerCount(event) {
        return this.listeners.get(event)?.size ?? 0;
    }
}
